import { useEffect, useState } from 'react';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import map from 'lodash/map';
import pull from 'lodash/pull';
import { useDataService } from '../../../../services/ApiDataService';
import useRecordContext from '../../../../utilities/RecordContextProviderUtilities';
import { serverDeviceTypeFormatter } from '../InfrastructureFormatters';
import { dateTimeFormatter } from '../../../../utilities';
import PropertiesGrid from '../../../form/PropertiesGrid';
import { CREDENTIAL_TIME_FIELD, DEVICE_TYPE_FIELD, UP_TIME_FIELD } from './DeviceConstants';
import DeviceProperties from './DeviceProperties';
import ServerProperties from './ServerProperties';
import { useDeviceInfraContext } from './DeviceInfraContext';

const LOOPBACK_IP = '127.0.0.1';
export const EMPTY_VALUE = '--';
const MS_PER_DAY = 86400000;

/**
 * Merges serverinfo key-value pairs with V2 device entity data
 * into a flat properties object for display.
 * Same pattern as NetworkDevicePropertiesPanel.buildPropertiesData.
 */
const buildPropertiesData = (serverInfoData, deviceData) => {
    const properties = mapValues(keyBy(serverInfoData, 'name'), 'value');
    const loopbacks = deviceData?.loopbackIps || [];
    const ipAddressesWithoutLoopback = pull(deviceData?.ipAddresses || [], LOOPBACK_IP);
    properties.ipAddresses = map(ipAddressesWithoutLoopback, (ip) => ({
        ipAddress: ip,
        loopback: loopbacks.includes(ip),
    }));

    // Preserve wireless controller reference for access point devices
    if (deviceData?.wirelessController) {
        properties.wirelessController = deviceData.wirelessController;
    }

    // Preserve device-level IP from V2 API for IP-based site lookup on new/unclassified devices
    properties.deviceIpAddress = deviceData?.ipAddress;
    properties.sites = deviceData?.sites;

    return properties;
};

/**
 * Fetches device properties from the query engine and V2 device API.
 * Properties (sysName, vendor, upTime, etc.) are NOT in the admin config —
 * they come from /query/serverinfo + /ngp/v2/devices.
 * @param {string} deviceId - Device UUID
 * @returns {Object|null} Properties object, or null while loading
 */
const useDeviceProperties = (deviceId) => {
    const dataService = useDataService();
    const [properties, setProperties] = useState(null);

    useEffect(() => {
        if (!deviceId) {
            return;
        }

        const now = Date.now();
        const serverInfoParams = {
            start: now - MS_PER_DAY,
            end: now,
            type: 'device',
            device: deviceId,
            metrics: ['status'],
        };

        Promise.all([
            dataService.get('/query/serverinfo', serverInfoParams),
            dataService.get(`/ngp/v2/devices/${deviceId}`),
        ])
            .then(([serverInfo, deviceData]) => {
                setProperties(buildPropertiesData(serverInfo?.data ?? [], deviceData ?? {}));
            })
            .catch(() => {
                setProperties({});
            });
    }, [dataService, deviceId]);

    return properties;
};

export const safeValue = (value) => value || EMPTY_VALUE;

/**
 * Safely formats credential time from serverinfo.
 * The value is a Unix timestamp (epoch millis) stored as a string or number.
 * Returns user-locale date-time string or '--' when invalid.
 */
const formatCredentialTime = (value) => {
    if (!value) {
        return EMPTY_VALUE;
    }
    const parsed = typeof value === 'number' ? value : Number.parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed <= 0) {
        return EMPTY_VALUE;
    }
    return dateTimeFormatter(parsed) || EMPTY_VALUE;
};

const DetailProperties = ({ properties }) => {
    const { type: deviceType } = useRecordContext();
    const displayDeviceType = properties[DEVICE_TYPE_FIELD]
        ? serverDeviceTypeFormatter(properties[DEVICE_TYPE_FIELD])
        : EMPTY_VALUE;
    const upTime = safeValue(properties[UP_TIME_FIELD]);
    const credentialTime = formatCredentialTime(properties[CREDENTIAL_TIME_FIELD]);
    const deviceData = {
        properties,
        displayDeviceType,
        upTime,
        credentialTime,
        deviceType,
    };
    const { isServer } = useDeviceInfraContext();

    if (isServer) {
        return <ServerProperties {...deviceData} />;
    }

    return <DeviceProperties {...deviceData} />;
};

/**
 * Displays device properties in label-value pairs.
 * Used for all device types EXCEPT server.
 * Fetches data from query engine + V2 device API (same as NetworkDevicePropertiesPanel).
 */
const DeviceDetailProperties = () => {
    const { id: deviceId } = useRecordContext();
    const properties = useDeviceProperties(deviceId);

    if (!properties) return null;

    return (
        <PropertiesGrid>
            <DetailProperties properties={properties} />
        </PropertiesGrid>
    );
};

export default DeviceDetailProperties;
